import { AuthContext, BaseNavLink, SidebarDivider, SidebarItem } from '@enter/core';
import { mdiMenuRight } from '@mdi/js';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { IInjectedRootStore } from 'stores';
import * as S from './main.sidebar.styles';

type IMainSidebarProps = IInjectedRootStore;

export const MainSidebar: React.FunctionComponent<IMainSidebarProps> = inject('rootStore')(
    observer(
        (props: IMainSidebarProps): React.ReactElement => {
            const { rootStore } = props;
            const { messageStore } = rootStore;
            const { getUnreadTotal, unreadInboxTotal } = messageStore;
            const context = useContext(AuthContext);

            const isSettings = useRouteMatch('/settings');
            const isChargemasters = useRouteMatch('/chargemasters');
            const showChargemasters = !!isSettings || !!isChargemasters;

            const isPayments = useRouteMatch('/payments');
            const isTransactions = useRouteMatch('/transactions');
            const [clickedBilling, setClickedBilling] = useState(false);
            const showBilling = !!isPayments || !!isTransactions || clickedBilling;
            const toggleBilling = () => {
                setClickedBilling(!clickedBilling);
            };

            const clickTerminal = () => {
                const isSandbox = /sandbox/.test(process.env.REACT_APP_API_URL || '');
                const terminalLink = isSandbox ? 'https://terminal.sandbox.enter.health' : 'https://terminal.enter.health';

                const terminalScript = document.getElementById('enter-payments-script');

                if(!terminalScript) {
                    const script = document.createElement("script");
                    script.id = "enter-payments-script";
                    script.src = terminalLink + '/terminal.min.js';
                    script.async = true;

                    script.onload = () => {
                        setTimeout(() => {
                            launchTerminal();
                        }, 500);
                    };

                    document.body.appendChild(script);
                } else {
                    launchTerminal();
                }
            }

            const launchTerminal = () => {
                window.Terminal.launch({
                   token: context.getCurrentAccessToken(),
                   refreshToken: context.getCurrentRefreshToken()
                });
            }

            const [showCommunication, setShowCommunication] = useState(false);
            const toggleCommunication = () => {
                setShowCommunication(!showCommunication);
            };

            // NB: setup background poll to check for messages
            useEffect(() => {
                try {
                    context.cancelGroup && context.cancelGroup('messaging');
                    context.setGroup && context.setGroup('messaging');
                    getUnreadTotal();
                    const updateInterval = 3 * 60 * 1000;
                    const interval = setInterval(getUnreadTotal, updateInterval);
                    return (): void => clearInterval(interval);
                } catch (err) {
                    // bury exceptions
                }
            }, [context, getUnreadTotal]);

            return (
                <S.SideBar
                    visible={!rootStore.layoutStore.sidebarCollapsed}
                    onClose={() => rootStore.layoutStore.toggleSidebar(true)}>
                    <BaseNavLink to="/" exact={true}>
                        {(linkProps): React.ReactElement => (
                            <SidebarItem active={linkProps.active}>Dashboard</SidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <SidebarItem onClick={toggleBilling}>
                        <S.CollapseButton variant="text">
                            <S.CollapseIcon path={mdiMenuRight} size={1} open={showBilling} />
                        </S.CollapseButton>
                        <span>Billing</span>
                    </SidebarItem>
                    <S.SidebarCollapse in={showBilling} timeout="auto" unmountOnExit={false}>
                        <BaseNavLink to="/claims">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    Claims
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/documents">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    Documents
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/overpayments/pending">
                            {(linkProps): React.ReactElement => (
                                <SidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    Overpayments
                                </SidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/payments" exact={true}>
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    Payments
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <SidebarItem onClick={clickTerminal}>
                            <S.CollapseIndent />
                            <span>Terminal</span>
                        </SidebarItem>
                    </S.SidebarCollapse>
                    <SidebarDivider />
                    <SidebarDivider />
                    <SidebarItem onClick={toggleCommunication}>
                        <S.CollapseButton variant="text">
                            <S.CollapseIcon path={mdiMenuRight} size={1} open={showCommunication} />
                        </S.CollapseButton>
                        <span>Communications</span>
                        {unreadInboxTotal > 0 && <S.MessageCount>{unreadInboxTotal}</S.MessageCount>}
                    </SidebarItem>
                    <S.SidebarCollapse in={showCommunication} timeout="auto" unmountOnExit={false}>
                        <BaseNavLink to="/messaging">
                            {(linkProps): React.ReactElement => (
                                <SidebarItem active={linkProps.active}>
                                    Messaging{' '}
                                    {unreadInboxTotal > 0 && <S.MessageCount>{unreadInboxTotal}</S.MessageCount>}
                                </SidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/mail">
                            {(linkProps): React.ReactElement => (
                                <SidebarItem active={linkProps.active}>Mail</SidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/fax">
                            {(linkProps): React.ReactElement => (
                                <SidebarItem active={linkProps.active}>Fax</SidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/phone">
                            {(linkProps): React.ReactElement => (
                                <SidebarItem active={linkProps.active}>Phone calls</SidebarItem>
                            )}
                        </BaseNavLink>
                    </S.SidebarCollapse>
                    <SidebarDivider />
                    <BaseNavLink to="/eligibility">
                        {(linkProps): React.ReactElement => (
                            <SidebarItem active={linkProps.active}>Eligibility</SidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to="/reports">
                        {(linkProps): React.ReactElement => (
                            <SidebarItem active={linkProps.active}>Reports</SidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to="/settings">
                        {(linkProps): React.ReactElement => (
                            <SidebarItem active={linkProps.active}>
                                <S.CollapseButton variant="text">
                                    <S.CollapseIcon path={mdiMenuRight} size={1} open={showChargemasters} />
                                </S.CollapseButton>
                                <span>Settings</span>
                            </SidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to="/chargemasters">
                        {(linkProps): React.ReactElement => (
                            <S.SidebarCollapse in={showChargemasters} timeout="auto" unmountOnExit={false}>
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    Chargemasters
                                </S.CollapseSidebarItem>
                            </S.SidebarCollapse>
                        )}
                    </BaseNavLink>
                </S.SideBar>
            );
        },
    ),
);
